import { Box, Typography } from '@mui/material'
import { memo } from 'react'
import type {
  TEnumItem,
  TProcessingAction,
  TPublisherLyricsApprovalDraftReviewSong,
  TPublisherSong,
} from '@modules/types'
import { SongHeader } from '../approval/SongHeader'
import { LyricsFeeQuotedPrice } from './LyricsFeeQuotedPrice'

type SongItemApprovalLyricsProps = {
  song: TPublisherSong
  enums: TEnumItem[]
  onApprove: (songId: number) => void
  onReject: (songId: number, reason: string) => void
  onChangeStatus: (songId: number) => void
  isLoading: boolean
  initialLoading: boolean
  processingSongId: number | null
  processingAction: TProcessingAction
  draftReviewSong: TPublisherLyricsApprovalDraftReviewSong
  isEditing: boolean
  feeValue: string
  onFeeEdit: (songId: number) => void
  onFeeChange: (songId: number, value: string) => void
  onFeeSave: (songId: number) => void
  onFeeCancel: (songId: number) => void
  validationError?: string
}

const CONTAINER_STYLES = {
  bgcolor: 'background.default',
  mx: 2,
  gap: 2,
  pb: 4,
}

const CONTENT_STYLES = {
  p: 3,
  borderRadius: 1,
  bgcolor: 'background.default',
}

const SongComment = memo(({ comment }: { comment: string }) => (
  <>
    <Typography variant="caption" color="text.secondary" mb={0.5} mt={4}>
      Comment
    </Typography>
    <Typography variant="body1" color="text.secondary">
      {comment}
    </Typography>
  </>
))

export const SongItemApprovalLyrics = memo(
  ({
    song,
    enums,
    onApprove,
    onReject,
    onChangeStatus,
    isLoading,
    initialLoading,
    processingSongId,
    processingAction,
    draftReviewSong,
    isEditing,
    feeValue,
    onFeeEdit,
    onFeeChange,
    onFeeSave,
    onFeeCancel,
    validationError,
  }: SongItemApprovalLyricsProps) => {
    const isBlocked = Boolean(song.approvalDecision) || (isLoading && processingSongId === song.id)
    const isOtherSongProcessing =
      isLoading && processingSongId !== null && processingSongId !== song.id
    const isCounterOfferBlocked = isBlocked || isOtherSongProcessing
    const isActionButtonsBlocked = isBlocked || isOtherSongProcessing
    const songHasStatus =
      draftReviewSong.isApproved !== null ||
      Boolean(draftReviewSong.isCounterOffered || draftReviewSong.rejectReason === 'Counteroffer')

    return (
      <Box>
        <Box sx={CONTAINER_STYLES}>
          <Box sx={CONTENT_STYLES}>
            <SongHeader
              song={song}
              onApprove={() => onApprove(song.id)}
              onReject={(songId, reason) => onReject(songId, reason)}
              processingSongId={processingSongId}
              processingAction={processingAction}
              draftReviewSong={draftReviewSong}
              onChangeStatus={onChangeStatus}
              isLoading={isLoading}
              initialLoading={initialLoading}
              isBlocked={isActionButtonsBlocked}
            />
            {song.additionalComments && <SongComment comment={song.additionalComments} />}
          </Box>

          <LyricsFeeQuotedPrice
            song={song}
            enums={enums}
            initialLoading={initialLoading}
            isLoading={isLoading}
            processingSongId={processingSongId}
            isBlocked={isCounterOfferBlocked}
            songHasStatus={songHasStatus}
            draftReviewSong={draftReviewSong}
            isEditing={isEditing}
            feeValue={feeValue}
            onEdit={onFeeEdit}
            onChange={onFeeChange}
            onSave={onFeeSave}
            onCancel={onFeeCancel}
            validationError={validationError}
          />
        </Box>
      </Box>
    )
  },
)
